import React, { useState, useEffect } from 'react';
import { pdfjs } from 'react-pdf';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from '@/components/ui/button';
import { Progress } from "@/components/ui/progress";

import { createCustomer, uploadFile, sendWhatsappMessage, uploadContractPdf } from "@/integrations/supabase/services";
import CameraCapture from '@/components/rentals/CameraCapture';
import { generateContractPdf } from '@/lib/contractGenerator';
import { compressImage } from '@/lib/imageCompression';
import { cn } from "@/lib/utils";

import NombrePaso from './NombrePaso';
import CedulaPaso from './CedulaPaso';
import TelefonoPaso from './TelefonoPaso';
import ContratoPaso from './ContratoPaso';

// Configuración del Worker desde CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FormData {
  nombre: string;
  cedula: string;
  telefono: string;
  fotoCedula: string | null;
  fotoPerfil: string | null;
  aceptoTerminos: boolean;
}

const TOTAL_PASOS = 6; // Nombre, Cédula, Teléfono, Foto Cédula, Contrato, Foto Perfil

// Expresiones regulares para validación
const REGEX_NOMBRE = /^[a-zA-Z\sÁÉÍÓÚáéíóúÑñ]*$/; // Letras, espacios, acentos
const REGEX_NUMEROS = /^[0-9]*$/; // Solo números

const RegistroWizard: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pasoActual, setPasoActual] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    cedula: '',
    telefono: '',
    fotoCedula: null,
    fotoPerfil: null,
    aceptoTerminos: false,
  });
  const [errores, setErrores] = useState<Partial<Record<keyof FormData, string>>>({});
  const [animationDirection, setAnimationDirection] = useState<'next' | 'prev' | null>(null);
  
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Efecto para generar el PDF personalizado en tiempo real para el paso de revisión
  useEffect(() => {
    let active = true;
    let url = "";

    if (pasoActual === 5) {
      const generatePreview = async () => {
        setGeneratingPdf(true);
        try {
          const blob = await generateContractPdf({
            name: formData.nombre,
            idCard: parseInt(formData.cedula),
            phone: formData.telefono,
            idPhotoUrl: formData.fotoCedula,
          });
          
          if (active) {
            url = URL.createObjectURL(blob);
            setPdfUrl(url);
          }
        } catch (error) {
          console.error("Error generando la vista previa del contrato:", error);
        } finally {
          if (active) setGeneratingPdf(false);
        }
      };
      generatePreview();
    }

    return () => {
      active = false;
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [pasoActual, formData.nombre, formData.cedula, formData.telefono, formData.fotoCedula]);

  const validarPasoActual = (): boolean => {
    const nuevosErrores: Partial<Record<keyof FormData, string>> = {};
    let esValido = true;

    if (pasoActual === 1) {
      if (!formData.nombre.trim()) {
        nuevosErrores.nombre = 'Por favor, ingresa tu nombre.';
        esValido = false;
      } else if (!REGEX_NOMBRE.test(formData.nombre)) {
        nuevosErrores.nombre = 'Tu nombre solo debe contener letras, espacios y acentos.';
        esValido = false;
      }
    } else if (pasoActual === 2) {
      if (!formData.cedula.trim()) {
        nuevosErrores.cedula = 'Necesitamos tu número de cédula para identificarte.';
        esValido = false;
      } else if (!REGEX_NUMEROS.test(formData.cedula)) {
        nuevosErrores.cedula = 'La cédula solo debe contener números.';
        esValido = false;
      } else if (formData.cedula.length > 9) { // Longitud Cédula: 9
        nuevosErrores.cedula = 'La cédula no puede tener más de 9 dígitos.';
        esValido = false;
      }
    } else if (pasoActual === 3) {
      if (!formData.telefono.trim() || formData.telefono === '+58') {
        nuevosErrores.telefono = 'Por favor, danos un número de teléfono para contactarte.';
        esValido = false;
      } else if (!/^\+?[0-9]*$/.test(formData.telefono)) {
        nuevosErrores.telefono = 'El teléfono solo debe contener números.';
        esValido = false;
      } else if (formData.telefono.replace(/\+/g, '').length > 12) {
        nuevosErrores.telefono = 'El teléfono no parece ser válido.';
        esValido = false;
      }
    } else if (pasoActual === 4) {
      if (!formData.fotoCedula) {
        nuevosErrores.fotoCedula = 'Para verificar tu identidad, necesitamos una foto de tu cédula.';
        esValido = false;
      }
    } else if (pasoActual === 5) {
      if (!formData.aceptoTerminos) {
        nuevosErrores.aceptoTerminos = 'Debes aceptar los términos y condiciones para continuar.';
        esValido = false;
      }
    } else if (pasoActual === 6) {
      if (!formData.fotoPerfil) {
        nuevosErrores.fotoPerfil = 'Tu foto de perfil es obligatoria para verificar tu identidad.';
        esValido = false;
      }
    }
    setErrores(nuevosErrores);
    return esValido;
  };

  const handleNext = () => {
    if (validarPasoActual()) {
      if (pasoActual < TOTAL_PASOS) {
        setAnimationDirection('next');
        setPasoActual(prev => prev + 1);
        setErrores({});
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (pasoActual > 1) {
      setAnimationDirection('prev');
      setPasoActual(prev => prev - 1);
      setErrores({});
    }
  };

  // Create user mutation
  const createCustomerMutation = useMutation({
    mutationFn: createCustomer,
    onSuccess: (_, variables) => {
      setIsSubmitting(false);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ title: "¡Tu cuenta ha sido creada con éxito!" });

      const { id_card, name, contract_url } = variables.customerData;

      sendWhatsappMessage({
        cardID: id_card,
        template: "welcome",
        variables: [name],
        contract: true,
        contractUrl: contract_url || undefined
      }).catch(err => console.error("Error in WhatsApp send:", err));
    },
    onError: (error: any) => {
      setIsSubmitting(false);
      if (error.message?.includes('duplicate key') || error.message?.includes('UK_id_card')) {
        toast({
          title: "Cliente existente",
          description: "El cliente con esta cédula ya se encuentra registrado.",
          className: "bg-orange-500 text-white border-orange-600",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error al registrar tus datos",
          description: "Mil disculpas, al parecer ocurrió un error al registrar tus datos.",
        });
      }
    },
  });

  const handleSubmit = async () => {
    if (isSubmitting) return; // Guard against double submission
    if (!validarPasoActual()) {
      toast({ variant: "destructive", title: "Oops!", description: "Parece que hay algunos errores. Por favor, revísalos antes de continuar." });
      return;
    }

    setIsSubmitting(true);

    let finalIdPhotoUrl = "";
    let finalProfilePhotoUrl = "";
    let finalContractUrl = "";

    // Subir foto de cédula si existe
    if (formData.fotoCedula && formData.fotoCedula.startsWith('data:image')) {
      try {
        const fileName = `customer-id-${formData.cedula}-${Date.now()}.jpg`;
        finalIdPhotoUrl = await uploadFile(formData.fotoCedula, fileName);
      } catch (error) {
        setIsSubmitting(false);
        toast({ variant: "destructive", title: "Error al subir la foto de tu cédula", description: (error as Error).message });
        return; // Detener si falla la subida
      }
    }

    // Subir foto de perfil si existe
    if (formData.fotoPerfil && formData.fotoPerfil.startsWith('data:image')) {
      try {
        const fileName = `customer-profile-${formData.cedula}-${Date.now()}.jpg`;
        finalProfilePhotoUrl = await uploadFile(formData.fotoPerfil, fileName);
      } catch (error) {
        toast({ variant: "destructive", title: "Error al subir tu foto de perfil", description: (error as Error).message });
      }
    }

    // Generar y subir el PDF de contrato firmado/personalizado final
    try {
      const finalContractBlob = await generateContractPdf({
        name: formData.nombre,
        idCard: parseInt(formData.cedula),
        phone: formData.telefono,
        idPhotoUrl: finalIdPhotoUrl || formData.fotoCedula, // Usamos la URL pública o el base64 local si no se pudo subir
      });

      const contractFileName = `contract-${formData.cedula}-${Date.now()}.pdf`;
      finalContractUrl = await uploadContractPdf(finalContractBlob, contractFileName);
    } catch (error) {
      setIsSubmitting(false);
      console.error("Error al generar o subir el contrato PDF:", error);
      toast({ variant: "destructive", title: "Error al generar tu contrato", description: (error as Error).message });
      return; // Detener si falla la generación del contrato
    }

    createCustomerMutation.mutate({
      customerData: {
        name: formData.nombre,
        id_card: parseInt(formData.cedula),
        phone: formData.telefono,
        id_photo_url: finalIdPhotoUrl,
        profile_photo_url: finalProfilePhotoUrl,
        contract_url: finalContractUrl,
      },
      shouldReturnData: false,
    });
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    let processedValue = value;
    let currentError = '';

    if (field === 'nombre') {
      if (!REGEX_NOMBRE.test(value) && value.length > 0) {
        currentError = 'Tu nombre solo debe contener letras, espacios y acentos.';
      }
    } else if (field === 'cedula') {
      if (!REGEX_NUMEROS.test(value)) {
        processedValue = value.replace(/[^0-9]/g, '');
      }
      if (processedValue.length > 9) { // Longitud Cédula: 9
        processedValue = processedValue.slice(0, 9);
      }
    } else if (field === 'telefono') {
      // Permitimos el símbolo + y números para el teléfono
      if (!/^[0-9+]*$/.test(value)) {
        processedValue = value.replace(/[^0-9+]/g, '');
      }
      // Ajustamos el límite de longitud para incluir +58 (13 caracteres)
      if (processedValue.length > 13) {
        processedValue = processedValue.slice(0, 13);
      }
    }

    setFormData(prev => ({ ...prev, [field]: processedValue }));
    if (errores[field as keyof FormData] && currentError === '') {
      setErrores(prev => ({ ...prev, [field]: undefined }));
    } else if (currentError) {
      setErrores(prev => ({ ...prev, [field]: currentError }));
    }
  };

  const renderPaso = () => {
    if (createCustomerMutation.isSuccess) {
      return (
        <div className="text-center p-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-xl font-semibold mb-2">¡Todo listo! Tu registro está completo.</h2>
          <p>¡Gracias por unirte! Ya puedes disfrutar de nuestros servicios.</p>
          <Button onClick={() => {
            createCustomerMutation.reset(); // Resetea el estado de la mutación
            setPasoActual(1);
            setFormData({
              nombre: '',
              cedula: '',
              telefono: '',
              fotoCedula: null,
              fotoPerfil: null,
              aceptoTerminos: false,
            });
            setErrores({});
          }} className="mt-4">
            Crear otra cuenta
          </Button>
        </div>
      );
    }
    switch (pasoActual) {
      case 1:
        return (
          <NombrePaso
            nombre={formData.nombre}
            onNombreChange={(val) => handleInputChange('nombre', val)}
          />
        );
      case 2:
        return (
          <CedulaPaso
            cedula={formData.cedula}
            onCedulaChange={(val) => handleInputChange('cedula', val)}
          />
        );
      case 3:
        return (
          <TelefonoPaso
            telefono={formData.telefono}
            onTelefonoChange={(val) => handleInputChange('telefono', val)}
          />
        );
      case 4:
        return (
          <Card>
            <CardHeader>
              <CardTitle>Paso 4: Foto de tu Cédula</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <CameraCapture
                label="Toma una foto de tu cédula"
                onCapture={(imageSrc) => {
                  setFormData(prev => ({ ...prev, fotoCedula: imageSrc }));
                  if (errores.fotoCedula) setErrores(prev => ({ ...prev, fotoCedula: undefined }));
                }}
                facingMode="environment" // Usar cámara trasera para documentos
              />
              {formData.fotoCedula && <img src={formData.fotoCedula} alt="Previsualización Cédula" className="mt-2 max-h-40 mx-auto" />}
            </CardContent>
          </Card>
        );
      case 5:
        return (
          <ContratoPaso
            pdfUrl={pdfUrl || ""}
            aceptoTerminos={formData.aceptoTerminos}
            onAceptoTerminosChange={(val) => {
              setFormData(prev => ({ ...prev, aceptoTerminos: val }));
              if (errores.aceptoTerminos) setErrores(prev => ({ ...prev, aceptoTerminos: undefined }));
            }}
          />
        );
      case 6:
        return (
          <Card>
            <CardHeader>
              <CardTitle>Paso 6: Tu foto de perfil</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <CameraCapture
                label="Tómate una foto para tu perfil"
                onCapture={(imageSrc) => {
                  setFormData(prev => ({ ...prev, fotoPerfil: imageSrc }));
                  if (errores.fotoPerfil) setErrores(prev => ({ ...prev, fotoPerfil: undefined }));
                }}
                facingMode="user" // Usar cámara frontal para perfil
              />
              {formData.fotoPerfil && <img src={formData.fotoPerfil} alt="Previsualización Perfil" className="mt-2 max-h-40 mx-auto" />}
            </CardContent>
          </Card>
        );
      default:
        return <p>Paso desconocido</p>;
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      {!createCustomerMutation.isSuccess && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-center mb-2">¡Bienvenido/a! Vamos a crear tu cuenta</h1>
          <Progress value={(pasoActual / TOTAL_PASOS) * 100} className="w-full mb-1" />
          <p className="text-xs text-muted-foreground text-right">Paso {pasoActual} de {TOTAL_PASOS}</p>
        </div>
      )}

      <div className="relative overflow-hidden min-h-[220px]">
        <div
          key={pasoActual}
          className={cn(
            "w-full",
            animationDirection === 'next' && 'animate-slide-in-from-right',
            animationDirection === 'prev' && 'animate-slide-in-from-left',
          )}
          onAnimationEnd={() => setAnimationDirection(null)}
        >
          {generatingPdf && pasoActual === 5 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-lg border shadow-sm space-y-4">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-gray-500 font-medium">Personalizando tu contrato legal...</p>
            </div>
          ) : (
            renderPaso()
          )}
          
          <div className="mt-2 ml-2 text-sm text-destructive">
            {pasoActual === 1 && errores.nombre && <p>{errores.nombre}</p>}
            {pasoActual === 2 && errores.cedula && <p>{errores.cedula}</p>}
            {pasoActual === 3 && errores.telefono && <p>{errores.telefono}</p>}
            {pasoActual === 4 && errores.fotoCedula && <p>{errores.fotoCedula}</p>}
            {pasoActual === 5 && errores.aceptoTerminos && <p>{errores.aceptoTerminos}</p>}
            {pasoActual === 6 && errores.fotoPerfil && <p>{errores.fotoPerfil}</p>}
          </div>
        </div>
      </div>

      {!createCustomerMutation.isSuccess && (
        <CardFooter className="flex justify-between mt-1">
          {pasoActual > 1 ? (
            <Button variant="outline" onClick={handleBack} disabled={createCustomerMutation.isPending}>
              Volver
            </Button>
          ) : (
            <div />
          )}

          <Button onClick={handleNext} disabled={createCustomerMutation.isPending || isSubmitting || (pasoActual === 5 && generatingPdf)}>
            {(createCustomerMutation.isPending || isSubmitting) ? 'Creando tu cuenta...' : (pasoActual === TOTAL_PASOS ? 'Finalizar' : 'Siguiente')}
          </Button>
        </CardFooter>
      )}
    </div>
  );
};

export default RegistroWizard;